/* eslint-disable no-console */
import React, { useState, useEffect, useCallback } from 'react'
import { NavLink, useParams, useLocation, useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { get } from 'lodash'
import {
  ContentLoader,
  IconClipboard,
  Notification,
  NotFound,
  Page,
  Tab,
  Tabs,
  TabContent,
  TabsHeader,
  IconEdit,
  IconDelete,
  Button,
  ConfirmationPopup,
  PermissionMissingNotificationTitle
} from 'shared/components'
import { copyText, checkUserPermission } from 'shared/helpers'
import { userPermissions } from 'shared/constants'
import { getOrder, updateOrder } from 'src/order/actions'
import {
  CustomerDetails,
  LicensesList,
  OrderDetails,
  OrderManagers,
  AssignCustomerAccount
} from './components'
import './styles.scss'

const OrderContainer = () => {
  const canManageOrder = checkUserPermission(userPermissions.orders_write)
  const location = useLocation()
  const history = useHistory()
  const { orderId } = useParams()
  const companyID = useSelector(state => get(state, 'company.details.id'))
  const [isLoading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [order, setOrder] = useState(null)
  const [selectedTab, setSelectedTab] = useState(0)
  const [orderCustomerAccount, setOrderCustomerAccount] = useState(
    get(order, 'customer_account.id')
  )

  const [isAddToCustomerAccountModalOpen, setAddToCustomerAccountModalOpen] = useState(false)
  const [isDeleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false)
  const [isEditCustomerAccountModalOpen, setEditCustomerAccountModalOpen] = useState(false)

  // eslint-disable-next-line no-unused-vars
  const handleAddToCustomerAccountModal = () => {
    setAddToCustomerAccountModalOpen(!isAddToCustomerAccountModalOpen)
  }
  const handleDeleteConfirmation = () => {
    setDeleteConfirmationOpen(!isDeleteConfirmationOpen)
  }
  const handleEditCustomerAccountModal = () => {
    setEditCustomerAccountModalOpen(!isEditCustomerAccountModalOpen)
  }

  useEffect(() => {
    const queryParams = get(location, 'search')
    if (!queryParams) {
      setSelectedTab(0)
    }
    if (queryParams.indexOf('st=0') >= 0) {
      setSelectedTab(0)
    }
    if (queryParams.indexOf('st=1') >= 0) {
      setSelectedTab(1)
    }
    if (queryParams.indexOf('st=2') >= 0) {
      setSelectedTab(2)
    }
    if (queryParams.indexOf('st=3') >= 0) {
      setSelectedTab(3)
    }
  }, [location])

  const fetchOrder = useCallback(() => {
    getOrder(companyID, orderId)
      .then(res => {
        setOrder(get(res, 'data'))
        setOrderCustomerAccount(get(res, 'data.customer_account.id'))
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
        setNotFound(true)
      })
  }, [companyID, orderId])

  useEffect(() => {
    fetchOrder()
  }, [fetchOrder])

  const refreshOrder = () => {
    setLoading(true)
    setNotFound(false)
    getOrder(companyID, orderId)
      .then(res => {
        setOrder(get(res, 'data'))
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
        setNotFound(true)
      })
  }

  const handleFieldCopy = (val, desc) => {
    copyText(val)
    Notification('success', `${desc} ${__('copied to clipboard')}`)
  }

  const createOrderTitle = orderID => {
    const label = __('Order ID')
    return (
      <div className='order-title-container'>
        {__('Order')}
        &nbsp;-&nbsp;
        <span>{orderID}</span>
        <button
          type='button'
          className='clipboard-btn'
          onClick={() => handleFieldCopy(orderID, label)}
        >
          <IconClipboard width='21.5' height='20' viewBox='0 0 51.5 50' />
        </button>
      </div>
    )
  }

  if (isLoading) {
    return (
      <Page>
        <ContentLoader text={__('Getting order details')} />
      </Page>
    )
  }

  if (notFound) {
    return (
      <Page>
        <NotFound />
      </Page>
    )
  }

  const handleManageOrderClick = cb => {
    if (!canManageOrder) {
      Notification(
        'error',
        <PermissionMissingNotificationTitle permission={userPermissions.orders_write} />,
        __('Contact you account admin for support.')
      )
      return false
    }
    cb()
    return true
  }

  const handleRemoveCustomerAccount = () => {
    updateOrder(orderId, companyID, { customer_account: null })
      .then(() => {
        setLoading(false)
        setDeleteConfirmationOpen(false)
        setOrderCustomerAccount(null)
        refreshOrder()
        Notification('success', __('Customer Account removed from order'))
      })
      .catch(err => {
        setLoading(false)
        setDeleteConfirmationOpen(false)
        console.error('Error removing customer account:', err)
      })
  }

  return (
    <div className='OrderContainer'>
      {isDeleteConfirmationOpen && (
        <ConfirmationPopup
          closeCb={() => setDeleteConfirmationOpen(false)}
          confirmCb={handleRemoveCustomerAccount}
          title={__('Remove order from a Customer account')}
          confirmText={__('Remove')}
          theme='error'
          disabled={isLoading}
        >
          {__('Are you sure you want to remove order from a Customer account?')}
        </ConfirmationPopup>
      )}
      <Page
        description={
          <>
            <NavLink to={`/${companyID}/customers/${get(order, 'customer.id')}`}>
              {get(order, 'customer.email') ||
                get(order, 'customer.company_name') ||
                get(order, 'customer.reference')}
            </NavLink>
            {orderCustomerAccount ? (
              <div
                className='customer-account-wrapper'
                key={orderCustomerAccount || 'customer-account'}
              >
                <NavLink
                  to={`/${companyID}/customer_accounts/${get(order, 'customer_account.id')}`}
                >
                  {get(order, 'customer_account.name') ||
                    get(order, 'customer_account.code') ||
                    get(order, 'customer_account.id')}
                </NavLink>
                <IconEdit
                  height='20px'
                  width='20px'
                  onClick={() => handleManageOrderClick(handleEditCustomerAccountModal)}
                  disabled={isLoading}
                  className='icon-button--edit'
                />
                <IconDelete
                  height='20px'
                  width='20px'
                  onClick={() => handleManageOrderClick(handleDeleteConfirmation)}
                  disabled={isLoading}
                  className='icon-button--delete'
                />
              </div>
            ) : (
              <div>
                <Button
                  key={orderCustomerAccount || 'customer-account'}
                  onClick={() => handleManageOrderClick(handleAddToCustomerAccountModal)}
                  theme='default'
                  size='sm'
                >
                  {__('Add to Customer Account')}
                </Button>
              </div>
            )}
          </>
        }
        title={createOrderTitle(get(order, 'store_id'))}
      >
        <div className='OrderContainer-tabs-header'>
          <h2>{__('Order details')}</h2>
        </div>
        <div className='OrderContainer-tabs'>
          <Tabs
            defaultFocus
            selectedIndex={selectedTab}
            onSelect={tabIndex => {
              history.push({
                search: `?st=${tabIndex}`
              })
              setSelectedTab(tabIndex)
            }}
          >
            <TabsHeader>
              <Tab>{__('Order details')}</Tab>
              <Tab>{__('Customer')}</Tab>
              <Tab>{__('Licenses')}</Tab>
              <Tab>{__('License managers')}</Tab>
            </TabsHeader>
            <TabContent>
              <OrderDetails order={order} companyID={companyID} refetchOrder={fetchOrder} />
            </TabContent>
            <TabContent>
              <CustomerDetails order={order} refetchOrder={fetchOrder} companyID={companyID} />
            </TabContent>
            <TabContent>
              <LicensesList order={order} refetchOrder={fetchOrder} />
            </TabContent>
            <TabContent>
              <OrderManagers order={order} companyID={companyID} />
            </TabContent>
          </Tabs>
        </div>
      </Page>
      {isAddToCustomerAccountModalOpen && (
        <AssignCustomerAccount
          order={order}
          closeForm={handleAddToCustomerAccountModal}
          hasAccount={false}
          updateCustomer={id => {
            setOrderCustomerAccount(id)
            refreshOrder()
          }}
        />
      )}
      {isEditCustomerAccountModalOpen && (
        <AssignCustomerAccount
          order={order}
          currentCustomerAcc={get(order, 'customer_account')}
          closeForm={handleEditCustomerAccountModal}
          hasAccount
          customerAccountId={orderCustomerAccount}
          updateCustomer={id => {
            setOrderCustomerAccount(id)
            refreshOrder()
          }}
        />
      )}
    </div>
  )
}

export default OrderContainer
